import { createAsyncThunk } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import {
  showArchiveJobProgressSnackbar,
  showArchiveReadySnackbar,
} from 'components/ProjectBankArchiveJobsSnackbar/ProjectBankArchiveJobsSnackbar';
import { getProjectBankArchiveJobResult } from 'scenes/ProjectBank/services/getProjectBankArchiveJobResult.service';
import { startProjectBankArchiveJob } from 'scenes/ProjectBank/services/startProjectBankArchiveJob.service';
import { pollProjectBankArchiveJob } from 'scenes/ProjectBank/utils/pollProjectBankArchiveJob';
import { triggerSameTabDownload } from 'scenes/ProjectBank/utils/projectBankDownload';
import {
  archiveJobFinished,
  archiveJobProgress,
  archiveJobStarted,
} from '../projectBankArchiveJobs.slice';
import {
  clearStoredArchiveJob,
  readStoredArchiveJobs,
  saveStoredArchiveJob,
} from '../utils/archiveJobStorage';

const t = (key, options) => i18n.t(key, options);

/**
 * Not normal: entries missing from an archive mean broken data (rows without
 * objects, forms without PDFs), so this reads as an error and stays long
 * enough to be read. The two kinds are told apart because they mean
 * different things to the reader.
 */
const showIncompleteArchiveToast = (result) => {
  if (!(result.skippedFileCount > 0)) {
    return;
  }
  const skippedFormCount = result.skippedFormCount || 0;
  const missingFileCount = result.skippedFileCount - skippedFormCount;
  enqueueSnackbar(
    [
      t('bank.archive_incomplete'),
      missingFileCount > 0 &&
        t('bank.files_missing_from_archive', { count: missingFileCount }),
      skippedFormCount > 0 &&
        t('bank.forms_missing_pdf_from_archive', { count: skippedFormCount }),
      t('bank.archive_incomplete_see_list'),
    ]
      .filter(Boolean)
      .join(' '),
    { variant: 'error', autoHideDuration: 15000 }
  );
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The worker sends the scheduled email right after it writes the DONE
// marker; a message with large attachments takes a while to go out.
const EMAIL_OUTCOME_INTERVAL_MS = 2000;
const EMAIL_OUTCOME_BUDGET_MS = 90 * 1000;
// Jobs whose outcome is being awaited, so the two callers (the tracking on
// DONE, the schedule thunk for a job already DONE) never report it twice.
const emailOutcomeAwaited = new Set();

/**
 * After a PROJECT_EMAIL job is DONE and its email has been scheduled: waits
 * for the worker to record the send on the marker. Nothing is said when the
 * message went out (it shows up among the project's sent mail) or when the
 * send is merely slow; only a failure is reported, since the message will
 * not go out on its own then.
 * @param {object} params
 * @param {string} params.projectId
 * @param {string} params.archiveJobId
 */
export const awaitProjectBankArchiveEmailOutcome = async ({
  projectId,
  archiveJobId,
}) => {
  if (emailOutcomeAwaited.has(archiveJobId)) {
    return;
  }
  emailOutcomeAwaited.add(archiveJobId);

  const deadline = Date.now() + EMAIL_OUTCOME_BUDGET_MS;
  let result = null;

  while (Date.now() < deadline) {
    try {
      result = await getProjectBankArchiveJobResult(projectId, archiveJobId);
    } catch (error) {
      console.error('Archive email outcome poll failed:', error);
      result = null;
    }
    if (
      result?.status === 'DONE' &&
      (result.projectEmailSentAt || result.projectEmailError)
    ) {
      break;
    }
    await wait(EMAIL_OUTCOME_INTERVAL_MS);
  }

  emailOutcomeAwaited.delete(archiveJobId);

  if (result?.status === 'DONE' && result.projectEmailError) {
    enqueueSnackbar(
      t('bank.archive_email_send_failed_toast', {
        message: result.projectEmailError,
      }),
      { variant: 'error', autoHideDuration: 15000 }
    );
  }
};

/**
 * Follows one archive job to its end. A DOWNLOAD job keeps the progress
 * snackbar alive, feeds it the poll's progress and is downloaded when DONE
 * (offered with a button instead when `resumed` — found in storage on app
 * start). A PROJECT_EMAIL job runs without any snackbar of its own: the user
 * wrote its email in the composer and the worker sends it, so only a failure
 * is reported; the composer reads the job's progress from the store to fill
 * the archive's name, count and size into the message. Resumed jobs drop
 * their stored entry even on a timeout.
 */
export const trackProjectBankArchiveJob = createAsyncThunk(
  'projectBankArchiveJobs/track',
  async (
    {
      archiveJobId,
      projectId,
      startedAt,
      delivery = 'DOWNLOAD',
      resumed = false,
    },
    { dispatch, getState }
  ) => {
    const silent = delivery === 'PROJECT_EMAIL';
    const snackbarKey = silent
      ? null
      : showArchiveJobProgressSnackbar(archiveJobId);
    let result;

    try {
      result = await pollProjectBankArchiveJob({
        projectId,
        archiveJobId,
        startedAt,
        onTick: (tick) => {
          if (tick?.status === 'RUNNING') {
            dispatch(
              archiveJobProgress({
                archiveJobId,
                stage: tick.stage || 'BUILDING',
                doneFiles: tick.doneFiles || 0,
                totalFiles: tick.totalFiles || 0,
                totalBytes: tick.totalBytes || null,
                doneParts: tick.doneParts || 0,
                totalParts: tick.totalParts || 0,
                // The archive's name, once the worker has collected the
                // entries; the project email composer fills it in.
                fileName: tick.fileName || null,
              })
            );
          }
        },
      });
    } finally {
      if (snackbarKey) {
        closeSnackbar(snackbarKey);
      }
    }

    if (result.status === 'DONE') {
      clearStoredArchiveJob(archiveJobId);
      dispatch(archiveJobFinished({ archiveJobId, status: 'DONE', result }));
      if (silent) {
        // Scheduled: the worker is sending. Not scheduled: the composer is
        // still open (its notice now says the message goes out at once) or
        // was closed for good; nothing to say either way.
        const { emailScheduled } =
          getState().projectBankArchiveJobs.jobs[archiveJobId] || {};
        if (emailScheduled) {
          await awaitProjectBankArchiveEmailOutcome({
            projectId,
            archiveJobId,
          });
        }
      } else if (resumed) {
        showArchiveReadySnackbar(result);
      } else {
        triggerSameTabDownload(result.downloadUrl);
        enqueueSnackbar(`${result.fileName} — ${t('bank.archive_ready')}`, {
          variant: 'success',
          autoHideDuration: 8000,
        });
      }
      showIncompleteArchiveToast(result);
    } else if (result.status === 'FAILED') {
      clearStoredArchiveJob(archiveJobId);
      dispatch(archiveJobFinished({ archiveJobId, status: 'FAILED' }));
      console.error('Archive job failed:', result.message);
      enqueueSnackbar(t('bank.archive_failed'), { variant: 'error' });
    } else {
      // TIMEOUT: the worker may still finish. A fresh job keeps its stored
      // entry so the next app start polls it once more; a resumed one has
      // already had that chance and is dropped. A PROJECT_EMAIL job needs no
      // word of it: the worker sends the message whenever it finishes.
      if (resumed) {
        clearStoredArchiveJob(archiveJobId);
      }
      dispatch(archiveJobFinished({ archiveJobId, status: 'TIMEOUT' }));
      if (!silent) {
        enqueueSnackbar(t('bank.archive_timeout'), { variant: 'warning' });
      }
    }

    return result;
  }
);

/**
 * Starts a tracked archive job: an in-browser download, or a link the user
 * sends on from the project email once it is ready. The size shown in the
 * snackbar comes from the worker's own marker once it has resolved the
 * entries, so it is always exact. Rejects when the job cannot be started.
 * @param {object} arg
 * @param {object} arg.request { projectId, fileIds, folderIds, formFiles }
 * @param {string} [arg.projectName]
 * @param {string} [arg.projectKey] the project email's route key; needed
 *   for PROJECT_EMAIL
 * @param {'DOWNLOAD'|'PROJECT_EMAIL'} [arg.delivery]
 */
export const startTrackedProjectBankArchiveJob = createAsyncThunk(
  'projectBankArchiveJobs/start',
  async (
    { request, projectName, projectKey, delivery = 'DOWNLOAD' },
    { dispatch }
  ) => {
    const { archiveJobId } = await startProjectBankArchiveJob({
      ...request,
      // The worker mints the tokenised link the email will carry.
      ...(delivery === 'PROJECT_EMAIL' ? { createDownloadLink: true } : {}),
    });
    const job = {
      archiveJobId,
      projectId: request.projectId,
      projectKey: projectKey || '',
      projectName: projectName || '',
      delivery,
      startedAt: Date.now(),
    };

    saveStoredArchiveJob(job);
    dispatch(archiveJobStarted(job));
    dispatch(trackProjectBankArchiveJob(job));
    return job;
  }
);

/**
 * On app start: every job left in storage by a reload, a closed tab or a
 * closed app is polled again. A finished DOWNLOAD job is offered with a
 * button, a running one within its budget is tracked, the rest are dropped;
 * a PROJECT_EMAIL job is tracked in silence as always.
 */
export const resumeStoredProjectBankArchiveJobs = createAsyncThunk(
  'projectBankArchiveJobs/resume',
  async (_, { dispatch, getState }) => {
    const stored = readStoredArchiveJobs();

    stored.forEach((job) => {
      if (getState().projectBankArchiveJobs.jobs[job.archiveJobId]) {
        return;
      }
      dispatch(archiveJobStarted(job));
      dispatch(trackProjectBankArchiveJob({ ...job, resumed: true }));
    });

    return stored.length;
  }
);
